import React, { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { trackPageView, trackEvent, EVENTS } from "@/lib/analytics";

const DEPTHS = [25, 50, 75, 90];

// Fires scroll_depth once per threshold per page.
function useScrollDepth(pathname) {
  useEffect(() => {
    const fired = new Set();
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      const pct = Math.round((window.scrollY / max) * 100);
      for (const d of DEPTHS) {
        if (pct >= d && !fired.has(d)) {
          fired.add(d);
          trackEvent(EVENTS.scrollDepth, { percent_scrolled: d, page_path: pathname });
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);
}

export function Layout() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Hydration of prerendered HTML is complete; later client-side navigations animate normally.
    window.__pmnPrerendered = false;
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Defer so useSeo has set document.title for the new route.
    const t = setTimeout(() => trackPageView(pathname), 0);
    return () => clearTimeout(t);
  }, [pathname]);

  useScrollDepth(pathname);

  return (
    <ReactLenis root options={{ lerp: 0.09, duration: 1.2, smoothWheel: true }}>
      <div className="App bg-paper">
        <a href="#main" className="skip-link">Skip to content</a>
        <Navbar />
        <main id="main">
          <Outlet />
        </main>
        <Footer />
      </div>
    </ReactLenis>
  );
}
